import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatStepperModule } from '@angular/material/stepper';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { UserService } from '../../../common/services/user.service';
import {  RegisterUser, UpdateUser, User } from '../../../common/models/user';
import { Role } from '../../../common/models/roles';
import { RoleService } from '../../../common/services/role.service';
import { MatIconModule } from '@angular/material/icon';
import { Subject, takeUntil } from 'rxjs';


@Component({
  selector: 'app-user-detail',
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatCardModule,
    MatStepperModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatIconModule,
    MatSelectModule
]
})
export class UserComponent implements OnInit {
  private destroy$ = new Subject<void>();

  isHandset: boolean = false;

  useryBasicInfoForm: FormGroup;
  isUpdating: boolean = false;
  hidePassword: boolean = true;
  userId!: string;

  roles: Role[] = [];

  private readonly snackbarConfig: MatSnackBarConfig = {
    duration: 3000,
    horizontalPosition: 'right',
    verticalPosition: 'top'
  };


  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private userService: UserService,
    private roleService: RoleService,
    private snackBar: MatSnackBar,
    private router: Router,
    private breakpointObserver: BreakpointObserver
  ) {

    // Step 1: Osnovni podaci
    this.useryBasicInfoForm = this.fb.group({
      userName: [null, Validators.required],
      password: [null],
      firstName: [null, [Validators.required]],
      lastName: [null, Validators.required],
      isActive: [false, Validators.required],
      roles: [[]]
    });
  }

  ngOnInit(): void {
    this.breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small]).pipe(takeUntil(this.destroy$)).subscribe(result => {
      this.isHandset = result.matches;
    });

    this.loadRoles();

    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      this.isUpdating = true;
      this.userId = idParam;
      this.loadUser(this.userId);
    } else {
      this.isUpdating = false;
      this.setRegistrationValidators();
    }
  }

  ngOnDestroy() {
      this.destroy$.next();
      this.destroy$.complete();
  }

  private setRegistrationValidators() {
    this.useryBasicInfoForm.get('password')?.setValidators(Validators.required);
  }

  private loadRoles() {
    this.roleService.getRoles().subscribe({
      next: (roles) => {
        this.roles = roles;
      },
      error: (err) => 
        console.error('Error loading roles', err)
    });
  }


  loadUser(id: string) {
    this.userService.getUser(id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (user: User) => {
        this.useryBasicInfoForm.patchValue({
          userName: user.userName,
          firstName: user.firstName,
          lastName: user.lastName,
          isActive: user.isActive,
          roles: user.roles
         
        });
      },
      error: (err) => {
        console.error('Error loading user', err);
      }
    });
  }

  onSubmit() {
    if (this.isUpdating) {
    this.updateUser();
    } else {
      this.registerUser();
    }
  }

  updateUser() {
    if (this.useryBasicInfoForm.invalid) {
      this.snackBar.open('Molimo ispunite sva obavezna polja prije spremanja.', 'Zatvori', this.snackbarConfig);
      return;
    }

    const dto: UpdateUser = {
      id: this.userId,
      ...this.useryBasicInfoForm.value
    };

    this.userService.updateUser(dto).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.snackBar.open('Uspješno ste ažurirali korisnika.', 'Zatvori', this.snackbarConfig);
        this.router.navigate(['administrator', 'users']);
      },
      error: (error) => {
        console.error('Error updating user', error);
        this.snackBar.open('Greška prilikom ažuriranja korisnika', 'Zatvori', this.snackbarConfig);
      }
    });
  }

  registerUser() {
    if (this.useryBasicInfoForm.invalid) {
      this.snackBar.open('Molimo ispunite sva obavezna polja prije spremanja.', 'Zatvori', this.snackbarConfig);
      return;
    }

    const dto: RegisterUser = {
      ...this.useryBasicInfoForm.value
    };

    this.userService.registerUser(dto).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.snackBar.open('Uspješno ste registrirali korisnika.', 'Zatvori', this.snackbarConfig);
        this.router.navigate(['administrator', 'users']);
      },
      error: (error) => {
        console.error('Error updating user', error);
        this.snackBar.open('Greška prilikom registracije korisnika', 'Zatvori', this.snackbarConfig);
      }
    });
  }
}